import { getCheckoutFlightItemFinalFare } from 'actions/CheckoutFlightActions'
import { fetchDateChangeJourneyV2, fetchFlightJourneyV2 } from 'actions/FlightActions'
import { fetchOrderItemFlightDetails } from 'actions/OrderActions'
import { getFlightItems } from 'checkout/selectors/view/flights'
import { expireFlightsTimer } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import getObjectKey from 'lib/object/getObjectKey'
import { useEffect, useMemo } from 'react'

export function useCheckoutFlightJourney() {
  const dispatch = useAppDispatch()
  const flightItems = useAppSelector(getFlightItems)
  const journeyErrors = useAppSelector(state => state.flights.journeyErrors)
  const postPurchaseMode = useAppSelector(state => findPostPurchaseCheckout(state.checkout.cart.mode))

  const hasAnyJourneyErrors = useMemo(() => {
    return flightItems.some(item => {
      return item.flights.some(journey => {
        if (journey) {
          const key = getObjectKey({
            journeyId: journey.journeyId,
            searchId: item.searchId,
            fareFamilyId: journey.fareFamily?.id,
          })

          if (journeyErrors[key]) return true
        }
      })
    })
  }, [flightItems, journeyErrors])

  useEffect(() => {
    if (hasAnyJourneyErrors) {
      // Might need refinement but if any journey has an error we expire the session as it cannot be recovered at the moment
      expireFlightsTimer()
    }
  }, [hasAnyJourneyErrors])

  useEffect(() => {
    if (postPurchaseMode === 'flight-change-dates') {
      return
    }

    flightItems
      .forEach(item => {
        if (item.searchId) {
          item.flights.forEach((flight) => {
            if (flight.journeyId) {
              dispatch(fetchFlightJourneyV2(item.searchId!, flight.journeyId, flight.fareFamily?.id))
            }
          })
        }
      })
  }, [flightItems, dispatch, postPurchaseMode])
}

export function useCheckoutFlightsDateChangeJourneys() {
  const dispatch = useAppDispatch()
  const flightItems = useAppSelector(getFlightItems)
  const journeys = useAppSelector(state => state.flights.journeysById)
  const postPurchaseMode = useAppSelector(state => findPostPurchaseCheckout(state.checkout.cart.mode))
  const existingOrder = useAppSelector(state => state.checkout.cart.existingOrder)

  const reservationId = useMemo(() => existingOrder?.flightItems?.[0]?.reservationId || '', [existingOrder])

  useEffect(() => {
    if (postPurchaseMode !== 'flight-change-dates') {
      return
    }

    flightItems
      .forEach(item => {
        if (item.searchId) {
          item.flights.forEach((flight) => {
            if (flight.journeyId) {
              // Include selected fare family ID if available
              const selectedUpsellOptionId = flight.fareFamily?.id
              dispatch(fetchDateChangeJourneyV2(reservationId, item.searchId!, flight.journeyId, selectedUpsellOptionId))
            }
          })
        }
      })
  }, [dispatch, flightItems, journeys, postPurchaseMode, reservationId])
}

function useCheckoutExistingOrderFlightDetails() {
  const dispatch = useAppDispatch()
  const existingOrder = useAppSelector(state => state.checkout.cart.existingOrder)
  const postPurchaseMode = useAppSelector(state => findPostPurchaseCheckout(state.checkout.cart.mode))
  const flightDetails = useAppSelector(state => state.orders.flightDetails)

  useEffect(() => {
    if (postPurchaseMode !== 'flight-change-dates' || !existingOrder) {
      return
    }

    // Fetch flight details for all flight items in the existing order
    // This is needed to get the change penalties/fees for the sidebar
    existingOrder.flightItems.forEach(flightItem => {
      const itemId = flightItem.id
      const hasDetails = flightDetails[itemId]

      if (!hasDetails) {
        dispatch(fetchOrderItemFlightDetails(existingOrder.id, itemId))
      }
    })
  }, [dispatch, existingOrder, postPurchaseMode, flightDetails])
}

function useCheckoutFlightFinalFares() {
  const dispatch = useAppDispatch()

  const flightItems = useAppSelector(getFlightItems)
  const journeys = useAppSelector(state => state.flights.journeysById)
  const postPurchaseMode = useAppSelector(state => findPostPurchaseCheckout(state.checkout.cart.mode))

  useEffect(() => {
    if (postPurchaseMode === 'flight-change-dates') {
      return
    }

    const items = flightItems
      .filter(item => {
        // first we make sure the item has all the data we need to fetch the final fares
        let hasItemData = item.flights.every((flight) => {
          // (item.fareType === 'oneWay' || !!item.returning?.journeyId)
          return flight.journeyId
        })

        hasItemData = hasItemData && !item.hasFinalFare && !item.fetchingFare && !item.fareFetchError
        return hasItemData
      })

    items.forEach((item, index) => {
      dispatch(getCheckoutFlightItemFinalFare(item, index))
    })
  }, [dispatch, flightItems, journeys, postPurchaseMode])
}

export default function useCheckoutFlightData() {
  useCheckoutFlightJourney()
  useCheckoutFlightFinalFares()
  useCheckoutFlightsDateChangeJourneys()
  useCheckoutExistingOrderFlightDetails()
}
